import { useEffect, useState } from "react";
import { ErrorToast } from "../Admin/Components/ToastComponent/RectHotToast";
import IsoToDateTime, { IsoToDateWithoutTime } from "./IsoDateTime";
import { Addition } from "./CalculationsWithDecimalValues";
import { AiFillLock } from "react-icons/ai";

const useProcessData = (
  data,
  setFormData,
  config,
  error,
  formData = [],
  Is_Subscription = false,
  setSumObj = () => {}
) => {
  const [loading, setLoading] = useState(false);

  // ──────────────────────────────────────────────────────────────────────────────
  // 1️⃣  If there's an error, show a toast
  // ──────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (error) {
      ErrorToast(error.message);
    }
  }, [error]);

  // ──────────────────────────────────────────────────────────────────────────────
  // 2️⃣  If dataKey is null or empty array, clear formData & stop loading
  // ──────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!config || !data) return;

    const arr = data[config.dataKey];
    if (arr == null || (Array.isArray(arr) && arr.length === 0)) {
      setFormData([]);
      setLoading(false);
    }
  }, [data, config?.dataKey, setFormData]);

  // ──────────────────────────────────────────────────────────────────────────────
  // 3️⃣  Main transform: map, date conversions, special cases, delete, etc.
  // ──────────────────────────────────────────────────────────────────────────────

  let makeobj = config?.AddKeys?.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
  let sum = {};
  useEffect(() => {
    if (!config || !data) return;

    setLoading(true);

    const {
      dataKey,
      extraDataKey,
      dateKeys = ["createdAt", "updatedAt"],
      dateKeysWithoutTime = [],
      deleteKeys = ["__typename"],
      specialCases = [],
      AddCol: addcol,
      calculateAge: AgeKey,
      AddKeys = [],
    } = config;
    // console.log(makeobj, "makeobj");
    // setSumObj(makeobj);
    sum = { ...makeobj };
    // Choose source array (either extraDataKey OR the raw dataKey array)
    const raw = data[dataKey] || [];

    const source = extraDataKey ? data[extraDataKey] : raw;

    const updated = source?.map((item) => {
      const obj = { ...item };

      // 1. Age
      if (AgeKey && obj[AgeKey]) {
        obj.Age = convertToAge(obj[AgeKey]);
      }

      if (AddKeys.length) {
        // console.log(sum, "sum");
        AddKeys.forEach((k) => {
          // console.log(obj[k], "value", sum[k], sum, k);
          if (obj[k]) {
            let add = Addition(Number(sum[k]), Number(obj[k]));
            // console.log(add, "aa1");
            sum[k] = add;
            // console.log(sum[k], "aa2", sum);
          }
        });
        // console.log(sum, "sum1");
      }

      // 2. Full-timestamp dates
      dateKeys.forEach((k) => {
        if (obj[k]) obj[k] = IsoToDateTime(obj[k]);
      });

      // 3. Date-only
      dateKeysWithoutTime.forEach((k) => {
        if (obj[k]) obj[k] = IsoToDateWithoutTime(obj[k]);
      });

      // 4. Special cases
      specialCases.forEach(({ condition, action }) => {
        if (condition(obj)) action(obj);
      });

      // 5. Additional computed columns
      addcol?.forEach(({ colName, value }) => {
        obj[colName] = value(obj);
      });

      // 6. Delete unwanted keys
      deleteKeys.forEach((k) => {
        delete obj[k];
      });

      // 6. Check Is_Lock or Unlock
      if ("Is_Lock" in obj) {
        const { Is_Lock, ...rest } = obj;
        return {
          L: Is_Lock ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AiFillLock />
            </div>
          ) : (
            ""
          ),
          ...rest,
        };
      }

      return obj;
    });
    // console.log(sum);
    // 7. Either append (subscription) or replace
    if (Is_Subscription) {
      setFormData((prev) => [...prev, ...updated]);
    } else {
      setSumObj(sum);
      setFormData(updated);
    }

    setLoading(false);
  }, [data, config, Is_Subscription, setFormData]);
  // console.log(sum);
  return { loading };
};

export default useProcessData;

export function convertToAge(inputDate) {
  console.log(inputDate, "inputDate");
  if (!inputDate) {
    return;
  }
  // Force treat the input as IST regardless of timezone info
  const dateParts = inputDate?.split("T");
  const hasMilliseconds = dateParts ? dateParts[1]?.includes(".") : false;
  const strippedInput = hasMilliseconds ? inputDate?.slice(0, 23) : inputDate;

  // Manually parse as if it's IST (local time), even if Z is present
  const [dateStr, timeStr] = strippedInput.replace("Z", "").split("T");
  const istTime = new Date(`${dateStr}T${timeStr}+05:30`);

  const now = new Date();

  const diffMs = now.getTime() - istTime.getTime();

  if (diffMs < 0) return "0 mins";

  const diffSecs = Math.round(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return `0 min`;
  else if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? "min" : "mins"}`;
  else if (diffHours < 24) {
    if (diffMins % 60 === 0) return `${diffHours} ${diffHours === 1 ? "hr" : "hrs"}`;
    else
      return `${diffHours} ${diffHours === 1 ? "hr" : "hrs"} ${diffMins % 60} ${diffMins % 60 === 1 ? "min" : "mins"}`;
  } else {
    if (diffHours % 24 === 0) return `${diffDays} ${diffDays === 1 ? "day" : "days"}`;
    else if (diffMins % 60 === 0)
      return `${diffDays} ${diffDays === 1 ? "day" : "days"} ${diffHours % 24} ${diffHours % 24 === 1 ? "hr" : "hrs"}`;
    else
      return `${diffDays} ${diffDays === 1 ? "day" : "days"} ${diffHours % 24} ${diffHours % 24 === 1 ? "hr" : "hrs"} ${
        diffMins % 60
      } ${diffMins % 60 === 1 ? "min" : "mins"}`;
  }
}
